import { Button, Input, Modal } from '@design-systems/canvas-ui'
import { useState, useMemo } from 'react'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useCreatePreferences } from 'hooks/user-context/useCreatePreferences'
import { useUpdatePreferences } from 'hooks/user-context/useUpdatePreferences'
import { useNavigate } from 'react-router-dom'
import { useLogClick } from 'utils/analytics'
import {
    getAllNavigationOptions,
    getAvailableNavigationOptions,
} from 'pages/settings/helpers'
import { ErrorAlert } from 'components/error-fallback'
import { safeTgtLogger } from 'utils/safeTgtLogger'
import { IAppContext } from 'types/user-context'
import './landingPageDialog.css'

interface LandingPageDialogProps {
    isOpen: boolean
}

export const LandingPageDialog = ({ isOpen }: LandingPageDialogProps) => {
    const {
        t,
        env,
        session,
        preferences,
        setPreferences,
        greenfieldGroups,
        locationType,
    } = useEssentials()
    const navigate = useNavigate()
    const { logClick } = useLogClick('landing-page-dialog')

    const [selectedPage] = useState<string>('/sales/overview')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const language =
        preferences?.user_contexts?.[0]?.app_context?.language || 'en'
    const preferredMap =
        preferences?.user_contexts?.[0]?.app_context?.preferredMap

    const { lanId } = session?.userInfo ?? {
        lanId: '',
    }

    const allNavigationOptions = useMemo(() => getAllNavigationOptions(t), [t])

    const availableNavigationOptions = useMemo(
        () =>
            getAvailableNavigationOptions({
                allOptions: allNavigationOptions,
                env,
                greenfieldGroups,
                locationType,
            }),
        [allNavigationOptions, env, greenfieldGroups, locationType],
    )

    const { createPreferences } = useCreatePreferences({
        lanId,
        env,
        accessToken: session?.accessToken ?? '',
    })

    const { updatePreferences } = useUpdatePreferences({
        lanId,
        env,
        accessToken: session?.accessToken ?? '',
        preferences,
    })

    const handleSuccess = (response: IAppContext) => {
        const newPreferences = {
            ...preferences,
            user_contexts: [response],
        }
        setPreferences(newPreferences)
        setIsSubmitting(false)
        setError(null)
        void navigate(selectedPage)
    }

    const handleError = (error: unknown) => {
        safeTgtLogger.error('Failed to save landing page preference', {
            error: {
                message: error instanceof Error ? error.message : String(error),
                type: error instanceof Error ? error.name : 'Unknown',
            },
            nonIndexedFields: {
                preferences: {
                    selectedPage,
                    hasExistingContext: preferences.user_contexts.length > 0,
                },
            },
        })
        setIsSubmitting(false)
        setError('Failed to save landing page preference. Please try again.')
    }

    const handleSubmit = () => {
        setIsSubmitting(true)
        setError(null)
        logClick(`set initial landing page to ${selectedPage}`)

        const payload = {
            language,
            ...(preferredMap && { preferredMap }),
            landingPage: selectedPage,
        }

        if (preferences.user_contexts.length === 0) {
            createPreferences(payload, {
                onSuccess: handleSuccess,
                onError: handleError,
            })
        } else {
            updatePreferences(payload, {
                onSuccess: handleSuccess,
                onError: handleError,
            })
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            headingText={t(TRANSLATION_KEYS.settings.landingPage)}
            className="hc8-ml-6x hc8-mr-6x hc8-ta-center landing-page-dialog-no-close"
        >
            <div className="hc8-pa-3x hc8-ta-center">
                <p className="hc8-mb-6x">
                    {t(TRANSLATION_KEYS.settings.landingPageDescription)}
                </p>

                {error && (
                    <ErrorAlert
                        variant="custom"
                        className="hc8-mb-6x"
                        onRetry={handleSubmit}
                    >
                        {error}
                    </ErrorAlert>
                )}

                <div className="hc8-mb-6x">
                    <Input.Select
                        id="landing-page-dialog-select"
                        value={selectedPage}
                        options={availableNavigationOptions.map((option) => ({
                            id: option.value,
                            value: option.value,
                            label: option.label,
                            disabled: false,
                        }))}
                        isDisabled={isSubmitting}
                        placeholder="Select an option"
                    />
                </div>

                <Button
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    variant="primary"
                >
                    {t(TRANSLATION_KEYS.common.continue)}
                </Button>
            </div>
        </Modal>
    )
}
